// Tool registry for the skin editor. Pure — no React.
//
// HOW TO ADD A TOOL
//   1. Add its id to the `Tool` union in types.ts.
//   2. Add an entry here. `TOOL_REGISTRY` is typed `{ [K in Tool]: ... }`, so the compiler refuses
//      a tool without one. Pick the `kind`:
//        - 'stroke' — drag-painted with the brush (size, mirror, gap-filling all come for free).
//                     Supply `pixelOp(src, ctx)`: given the ACTIVE layer's pixel as it was when the
//                     stroke began, return what it becomes. A shading tool (lighten/darken/noise)
//                     is a stroke tool whose pixelOp reads `src` (see shade.ts).
//        - 'shape'  — drag from a start to an end pixel; supply `points(x0, y0, x1, y1)`. Painted
//                     with the primary color, mirrored when mirror mode is on.
//        - 'fill'   — the bucket (one click, face-bounded flood fill).
//        - 'picker' — the eyedropper (reads the composite, changes no pixels).
//   3. Add its icon to `TOOL_ICONS` in app/tools/skin-editor/components/Toolbar.tsx (also a
//      compile-checked Record), and put it in `BASIC_TOOLS`, `SHAPE_TOOLS` or `SHADE_TOOLS` to show
//      a button (the last two are menus, so the toolbar does not grow).
//   Every 2D view and the 3D view dispatch by `kind`, so nothing else needs to change.

import type { Color, Tool, ShapeTool, ShadeTool, Point } from './types';
import { TRANSPARENT, linePoints, rectanglePoints, ellipsePoints } from './paint';
import { lightenPixel, darkenPixel, noisePixel } from './shade';
import { getMirrorPixel, type SkinModel } from './skinMap';

export interface PixelContext {
  /** The primary color. */
  color: Color;
  x: number;
  y: number;
  /** Shading strength, 0..1 in OKLab lightness (0.1 = "10%"). */
  strength: number;
  /** Per-stroke seed (the Noise brush). Same seed + same pixel = same result. */
  seed: number;
  model: SkinModel;
  mirror: boolean;
}

/**
 * The coordinate the Noise brush hashes. With mirror mode on, a pixel and its mirror image hash the
 * SAME coordinate, so both sides get the same noise pattern (a mirrored stroke looks mirrored). With
 * mirror off, every pixel hashes itself — canonicalizing always would make every noise stroke on the
 * head or body left-right symmetric.
 */
function noiseKey(ctx: PixelContext): { x: number; y: number } {
  if (!ctx.mirror) return ctx;
  const m = getMirrorPixel(ctx.x, ctx.y, ctx.model);
  if (!m) return ctx;
  return m.y * 64 + m.x < ctx.y * 64 + ctx.x ? m : ctx;
}

interface ToolBase {
  label: string;
  /** Single-key shortcut (lower-case). `shift` = the key must be pressed with Shift. */
  shortcut?: { key: string; shift?: boolean };
  /** One-line hint shown under the 2D editor. */
  hint: string;
}

export interface StrokeToolDef extends ToolBase {
  kind: 'stroke';
  pixelOp: (src: Color, ctx: PixelContext) => Color;
}
export interface ShapeToolDef extends ToolBase {
  kind: 'shape';
  points: (x0: number, y0: number, x1: number, y1: number) => Point[];
}
export interface FillToolDef extends ToolBase { kind: 'fill' }
export interface PickerToolDef extends ToolBase { kind: 'picker' }

export type ToolDefinition = StrokeToolDef | ShapeToolDef | FillToolDef | PickerToolDef;
export type ToolKind = ToolDefinition['kind'];

export const TOOL_REGISTRY: { [K in Tool]: ToolDefinition } = {
  pencil: {
    kind: 'stroke', label: 'Pencil', shortcut: { key: 'b' }, hint: 'Click and drag to paint',
    pixelOp: (_src, ctx) => ctx.color,
  },
  eraser: {
    kind: 'stroke', label: 'Eraser', shortcut: { key: 'e' }, hint: 'Click and drag to erase (makes pixels transparent)',
    pixelOp: () => TRANSPARENT,
  },
  fill: { kind: 'fill', label: 'Fill', shortcut: { key: 'g' }, hint: 'Click to fill an area of one face' },
  eyedropper: { kind: 'picker', label: 'Color picker', shortcut: { key: 'i' }, hint: 'Click to pick a color' },
  line: {
    kind: 'shape', label: 'Line', shortcut: { key: 'l' }, hint: 'Click and drag to draw a line',
    points: (x0, y0, x1, y1) => linePoints(x0, y0, x1, y1),
  },
  rectangle: {
    kind: 'shape', label: 'Rectangle', shortcut: { key: 'r' }, hint: 'Click and drag to draw a rectangle outline',
    points: (x0, y0, x1, y1) => rectanglePoints(x0, y0, x1, y1, false),
  },
  filledRectangle: {
    kind: 'shape', label: 'Filled rectangle', shortcut: { key: 'r', shift: true }, hint: 'Click and drag to draw a filled rectangle',
    points: (x0, y0, x1, y1) => rectanglePoints(x0, y0, x1, y1, true),
  },
  ellipse: {
    kind: 'shape', label: 'Ellipse', shortcut: { key: 'o' }, hint: 'Click and drag to draw an ellipse outline',
    points: (x0, y0, x1, y1) => ellipsePoints(x0, y0, x1, y1, false),
  },
  filledEllipse: {
    kind: 'shape', label: 'Filled ellipse', shortcut: { key: 'o', shift: true }, hint: 'Click and drag to draw a filled ellipse',
    points: (x0, y0, x1, y1) => ellipsePoints(x0, y0, x1, y1, true),
  },
  lighten: {
    kind: 'stroke', label: 'Lighten', shortcut: { key: 'd', shift: true }, hint: 'Drag over painted pixels to lighten them (hue kept)',
    pixelOp: (src, ctx) => lightenPixel(src, ctx.strength),
  },
  darken: {
    kind: 'stroke', label: 'Darken', shortcut: { key: 'd' }, hint: 'Drag over painted pixels to darken them (hue kept)',
    pixelOp: (src, ctx) => darkenPixel(src, ctx.strength),
  },
  noise: {
    kind: 'stroke', label: 'Noise', shortcut: { key: 'n' }, hint: 'Drag over painted pixels to vary their shade at random',
    pixelOp: (src, ctx) => { const k = noiseKey(ctx); return noisePixel(src, k.x, k.y, ctx.seed, ctx.strength); },
  },
};

/** Tools with their own toolbar button, in order. */
export const BASIC_TOOLS: readonly Tool[] = ['pencil', 'eraser', 'fill', 'eyedropper'];
/** Tools grouped under the toolbar's Shapes menu, in order. */
export const SHAPE_TOOLS: readonly ShapeTool[] = ['line', 'rectangle', 'filledRectangle', 'ellipse', 'filledEllipse'];
/** Tools grouped under the toolbar's Shading menu (with the strength slider), in order. */
export const SHADE_TOOLS: readonly ShadeTool[] = ['lighten', 'darken', 'noise'];

export function isShadeTool(id: Tool): id is ShadeTool {
  return (SHADE_TOOLS as readonly Tool[]).includes(id);
}

export function getTool(id: Tool): ToolDefinition {
  return TOOL_REGISTRY[id];
}

export function isShapeTool(id: Tool): id is ShapeTool {
  return TOOL_REGISTRY[id].kind === 'shape';
}

export function isStrokeTool(id: Tool): boolean {
  return TOOL_REGISTRY[id].kind === 'stroke';
}

/** "Pencil (B)", "Filled ellipse (Shift+O)". */
export function toolTitle(id: Tool): string {
  const t = TOOL_REGISTRY[id];
  if (!t.shortcut) return t.label;
  return `${t.label} (${t.shortcut.shift ? 'Shift+' : ''}${t.shortcut.key.toUpperCase()})`;
}

/** The tool bound to a key press, or null. `key` is KeyboardEvent.key. */
export function toolForShortcut(key: string, shift: boolean): Tool | null {
  const k = key.toLowerCase();
  for (const id of Object.keys(TOOL_REGISTRY) as Tool[]) {
    const s = TOOL_REGISTRY[id].shortcut;
    if (s && s.key === k && Boolean(s.shift) === shift) return id;
  }
  return null;
}
